import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import {
  Box, Divider, Grid, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import SensorsIcon from '@mui/icons-material/Sensors';
import {
  InferencesIcon, DeploymentsIcon, ModelsIcon, SegmentsIcon,
} from '../global/CustomIcons';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ShowChartIcon from '@mui/icons-material/ShowChart';

import {
  KPICard, Surface, StatusDot, formatNumber,
} from '../dashboard/components/DashboardPrimitives';
import { useDisplayNames } from '../../utils/displayNames';
import { modelDisplayName } from './modelNames';
import { fetchRequest as forecastFetch } from '../../redux/actions/discovery/studioForecast';
import { FanChart, ScoreBars } from '../common/ForecastCharts';
import AccuracyInfoTooltip from '../common/AccuracyInfoTooltip';

// Classification goals (anomaly / trend) emit a probability in [0,1], not a
// level — render score bars, never the fan chart (same rule as the Forecast
// page, so the Overview preview always matches the full view).
const SCORE_SERVING_MODES = new Set(['anomaly_score', 'trend_direction']);
const isScoreForecast = (data) => {
  const sm = data?.forecast?.serving_mode;
  if (sm) return SCORE_SERVING_MODES.has(sm);
  return data?.goal_type === 'anomaly' || data?.goal_type === 'trend';
};
const isAnomalyMode = (data) => {
  const sm = data?.forecast?.serving_mode;
  if (sm) return sm === 'anomaly_score';
  return data?.goal_type === 'anomaly';
};

const STATE_TONE = {
  done: 'success',
  active: 'primary',
  review: 'warning',
  blocked: 'warning',
  failed: 'error',
  off: 'neutral',
  pending: 'neutral',
};

const STATE_LABEL = {
  done: 'Done',
  active: 'In progress',
  review: 'Needs you',
  blocked: 'Blocked',
  failed: 'Failed',
  off: 'Off',
  pending: 'Waiting',
};

const safeRelative = (d) => {
  try { if (!d) return null; return `${formatDistanceToNow(new Date(d))} ago`; }
  catch (_e) { return null; }
};

// Relative label for the next scheduled run; collapses past/imminent to "Due now".
const nextRunLabel = (d) => {
  try {
    if (!d) return null;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    if (dt.getTime() - Date.now() <= 1000) return 'Due now';
    return `in ${formatDistanceToNow(dt)}`;
  } catch (_e) { return null; }
};

// Keep the KPI headline on one line — long text becomes "…" rather than
// wrapping the tile into a different height.
const Headline = ({ children, sx }) => (
  <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...sx }}>
    {children}
  </Box>
);

const shortId = (id) => {
  if (!id) return null;
  const s = String(id);
  return s.length > 8 ? s.slice(-8) : s;
};

/* One artifact behind the goal — its id and a click-through to the full
   detail page. Rendered as a compact tile in the Resources grid so the four
   artifacts share one row on desktop. Disabled (muted, non-clickable) until
   the id exists. */
const ResourceRow = ({ icon, label, id, to, navigate }) => {
  const theme = useTheme();
  const enabled = Boolean(id && to);
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      onClick={enabled ? () => navigate(to) : undefined}
      sx={{
        py: 1,
        px: 1.25,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        minWidth: 0,
        cursor: enabled ? 'pointer' : 'default',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': enabled ? {
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          borderColor: alpha(theme.palette.primary.main, 0.3),
        } : undefined,
      }}
    >
      <Box sx={{
        width: 28, height: 28, borderRadius: 1, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(theme.palette.text.primary, enabled ? 0.06 : 0.03),
        color: enabled ? 'text.secondary' : 'text.disabled',
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {label}
        </Typography>
        {enabled ? (
          <Tooltip title={String(id)} arrow placement="top-start">
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontFamily: 'monospace', lineHeight: 1.3 }}
              noWrap
            >
              {shortId(id)}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            —
          </Typography>
        )}
      </Box>
      {enabled && (
        <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
      )}
    </Stack>
  );
};

/**
 * The goal Overview dashboard, built from the shared dashboard primitives
 * (KPICard / Surface) so it matches the main dashboard's visual language:
 * four hero KPI tiles (drivers, best model, deployment, inferences) and a
 * serving summary. Stats come from the polled activity response.
 */
const fmtPct = (v) => (
  typeof v === 'number' && !Number.isNaN(v) ? `${v.toFixed(1)}%` : null
);

const GoalDashboard = ({ goalId, goal, activity, totalCost, navigate }) => {
  const theme = useTheme();
  const nameOf = useDisplayNames();
  const dispatch = useDispatch();
  const stats = activity?.stats || {};
  const phases = useMemo(() => activity?.phases || [], [activity]);
  const phaseState = (id) => phases.find((p) => p.id === id)?.state || 'pending';

  const drivers = stats.drivers || {};
  const model = stats.model || {};
  const deploy = stats.deploy || {};
  const serving = stats.serving || {};
  const resources = activity?.resources || {};

  const go = (seg) => () => navigate(`/goals/${goalId}/${seg}`);

  const driversState = phaseState('discover');
  const modelStateKey = model.state || phaseState('fit');
  const deployStateKey = deploy.state || phaseState('deploy');

  const driversValidated = drivers.validated ?? 0;
  const inferenceCount = serving.inference_count ?? 0;
  const lastInfer = safeRelative(serving.last_inference_at);
  // Live goals re-forecast on a cadence; historical goals run on demand only.
  const servingLive = serving.live !== false;
  const nextInfer = servingLive ? nextRunLabel(serving.next_inference_at) : 'On demand';

  const modelAccPct = fmtPct(model.accuracy);
  const modelSkillPct = fmtPct(model.skill);
  // Forecast preview: pull the champion's latest served forecast (shared
  // studio slice). Guard on goal_id so a stale response from a previously
  // viewed goal never renders under this one.
  const fcState = useSelector((s) => s.discovery_studio_forecast);
  const fcResponse = fcState?.response;
  const fcLoading = Boolean(fcState?.isLoading);
  const forecastPreview = (fcResponse && fcResponse.goal_id === goalId)
    ? fcResponse.forecast : null;
  const deployLive = deploy.live;
  useEffect(() => {
    if (goalId && deployLive) dispatch(forecastFetch({ goalId }));
  }, [dispatch, goalId, deployLive]);

  const deploySub = deploy.live
    ? 'Live and serving'
    : (STATE_LABEL[deployStateKey] || 'Not deployed');

  return (
    <Stack spacing={2}>
      {/* ── KPI tiles ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        <KPICard
          tone={STATE_TONE[driversState] || 'neutral'}
          icon={<BubbleChartIcon sx={{ fontSize: 18 }} />}
          label="Drivers"
          primary={(
            <Headline>
              {formatNumber(driversValidated)}
              {drivers.exploratory ? (
                <span style={{ opacity: 0.5, fontSize: '0.5em', marginLeft: 6 }}>
                  +{drivers.exploratory}
                </span>
              ) : null}
            </Headline>
          )}
          subtitle={drivers.exploratory
            ? `${drivers.exploratory} suggestive`
            : (driversValidated ? 'validated' : 'none yet')}
          onShortcut={go('drivers')}
          shortcutTitle="Open Driver Discovery"
        />

        <KPICard
          tone={STATE_TONE[modelStateKey] || 'neutral'}
          icon={<ModelsIcon sx={{ fontSize: 18 }} />}
          label="Model Accuracy"
          primary={(
            <AccuracyInfoTooltip
              value={typeof model.accuracy === 'number' ? model.accuracy : null}
              placement="top-start"
              note={model.label
                ? `Champion: ${[modelDisplayName(model), model.tech_label]
                  .filter(Boolean)
                  .filter((v, i, xs) => xs.indexOf(v) === i)
                  .join(' — ')}`
                : null}
            >
              <Box sx={{ cursor: 'help' }}>
                <Headline>
                  {modelAccPct
                    || (model.label
                      ? '—'
                      : (modelStateKey === 'active' ? 'Fitting…' : '—'))}
                </Headline>
              </Box>
            </AccuracyInfoTooltip>
          )}
          subtitle={model.label
            ? (modelSkillPct ? `Skill ${modelSkillPct}` : 'Champion model')
            : (STATE_LABEL[modelStateKey] || 'Awaiting fit')}
          onShortcut={go('models')}
          shortcutTitle="Open Models"
        />

        <KPICard
          tone={inferenceCount > 0 ? 'success' : 'neutral'}
          icon={<InferencesIcon sx={{ fontSize: 18 }} />}
          label="Inferences"
          primary={<Headline>{formatNumber(inferenceCount)}</Headline>}
          subtitle={lastInfer ? `last ${lastInfer}` : (deploy.live ? 'none yet' : 'after deploy')}
          onShortcut={go('serving')}
          shortcutTitle="Open Serving"
        />
      </Box>

      {/* ── Forecast preview ──────────────────────────────────────────── */}
      {deploy.live && (
        <Surface sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{
                width: 28, height: 28, borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
              }}>
                <ShowChartIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
                Forecast
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              onClick={go('forecast')}
              sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
            >
              Open
            </Typography>
          </Stack>
          {forecastPreview?.point?.length ? (
            isScoreForecast(fcResponse) ? (
              <ScoreBars
                forecast={forecastPreview}
                anomaly={isAnomalyMode(fcResponse)}
                height={190}
              />
            ) : (
              <FanChart forecast={forecastPreview} height={190} />
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              {fcLoading
                ? 'Loading the latest forecast…'
                : 'No forecast served yet — it appears here once the model runs.'}
            </Typography>
          )}
        </Surface>
      )}

      {/* ── Serving summary ───────────────────────────────────────────── */}
      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{
              width: 28, height: 28, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(deploy.live ? theme.palette.success.main : theme.palette.text.secondary, 0.12),
              color: deploy.live ? 'success.main' : 'text.secondary',
            }}>
              <DeploymentsIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
              Serving
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StatusDot tone={deploy.live ? 'success' : 'neutral'} size={7} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {deploy.live ? 'Serving' : 'Not deployed'}
              </Typography>
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            onClick={go('serving')}
            sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            Open
          </Typography>
        </Stack>

        {deploy.live ? (
          <Grid container spacing={2}>
            {[
              { label: 'Inferences', value: formatNumber(inferenceCount) },
              { label: 'Last inference', value: lastInfer || '—' },
              { label: 'Next inference', value: nextInfer || '—' },
              { label: 'Model', value: model.label ? modelDisplayName(model) : '—' },
            ].map((f) => (
              <Grid item xs={6} sm={3} key={f.label}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{f.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25, textTransform: f.cap ? 'capitalize' : 'none' }} noWrap>
                  {f.value}
                </Typography>
              </Grid>
            ))}
            {goal?.target_key && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Serving <strong>{nameOf(goal.target_key)}</strong>
                  {typeof goal.horizon_bins === 'number' ? <> · {goal.horizon_bins} steps ahead</> : null}
                </Typography>
              </Grid>
            )}
          </Grid>
        ) : (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Not serving yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deploySub}. Deploy the best model to start serving live forecasts.
              </Typography>
            </Box>
            <Typography
              variant="caption"
              onClick={go('models')}
              sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 700, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.25, '&:hover': { textDecoration: 'underline' } }}
            >
              Review &amp; deploy <NorthEastIcon sx={{ fontSize: 13 }} />
            </Typography>
          </Stack>
        )}

      </Surface>

      {/* ── Resources — the concrete artifacts behind this goal ───────── */}
      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.text.secondary, 0.1),
            color: 'text.secondary',
          }}>
            <LayersIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
            Resources
          </Typography>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          <ResourceRow
            icon={<ModelsIcon sx={{ fontSize: 16 }} />}
            label="Model"
            id={resources.model_id}
            to={resources.model_id ? `/model/${resources.model_id}` : null}
            navigate={navigate}
          />
          <ResourceRow
            icon={<SegmentsIcon sx={{ fontSize: 16 }} />}
            label="Segment"
            id={resources.segment_id}
            to={resources.segment_id ? `/segments/${resources.segment_id}` : null}
            navigate={navigate}
          />
          <ResourceRow
            icon={<SensorsIcon sx={{ fontSize: 16 }} />}
            label="Deployment"
            id={resources.deployment_id}
            to={resources.deployment_id ? `/deployment/${resources.deployment_id}` : null}
            navigate={navigate}
          />
          <ResourceRow
            icon={<InferencesIcon sx={{ fontSize: 16 }} />}
            label="Inference"
            id={resources.last_inference_id}
            to={resources.last_inference_id ? `/inferences/${resources.last_inference_id}` : null}
            navigate={navigate}
          />
        </Box>
      </Surface>

      {/* ── Total cost incurred by this goal ──────────────────────────── */}
      {totalCost != null && (
        <Surface sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                color: 'text.secondary',
              }}>
                <PaidOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary', lineHeight: 1.2 }}>
                  Total cost
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                  Lifetime spend on this goal
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ flexShrink: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                {Number(totalCost).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                cr
              </Typography>
            </Stack>
          </Stack>
        </Surface>
      )}
    </Stack>
  );
};

export default GoalDashboard;
